import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import { i18n } from '@lingui/core';
import { Button } from '@material-ui/core';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import Panel from '../../page-framework/panel/PanelDecorator';
import { useDataService } from '../../../services/ApiDataService';
import { useFindById } from '../../../services/FormQueryService';
import { useFormSaveService } from '../../../services/FormSaveService';
import { useNavigate } from '../../../utilities/PgmUtilities';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import { useUser } from '../../../stores/SessionStore';

import useAlerts from '../../../stores/AlertsStore';
import { EMAIL_SEPARATOR } from '../../pages-admin/general/Constants';
import { VoipPulseTestConfig } from './VoipPulseTestConfig';
import {
    DEFAULT_DURATION,
    DEFAULT_JITTER,
    DEFAULT_RING_DURATION,
    EMPTY_RESULTS_OBJECT,
    INITIAL_PHONE_STATUS,
    INITIAL_TEST_STATUS,
    VOIP_PULSE_TEST_TYPE,
} from './VoipTestConstants';
import { isEmailValid } from '../../../utilities/FormUtilities';
import { splitEmailString } from '../../../utilities/StringUtilities';
import { useDetailStyles } from './VoipTestNowStyles';
import { buildLinkUrl } from '../../links/Link';
import { QUICK_TEST_STATUS } from '../quick-test-panel/QuickTestConstants';

const nPointsListApiUrl = '/query/admin/agents/picker';
const quickTestsApiUrl = '/ipm/v1/admin/quicktests';
const testTypeApiUrl = '/ipm/v1/open/testTypes';
const defaultValues = {
    testConfig: {
        duration: DEFAULT_DURATION,
        ringDuration: DEFAULT_RING_DURATION,
        jitter: DEFAULT_JITTER,
        preferredCodec: '*',
    },
};

function VoipTestNowDetailPanel({ reRun, tableSelection, submitRef, setBreadcrumbs }) {
    const classes = useDetailStyles();
    const [user] = useUser();
    const [, /* Not used */ alertsActions] = useAlerts();
    const userName = useMemo(() => `${user.firstName} ${user.lastName}`, [user]);
    const [loading, setLoading] = useState(true);
    const methods = useForm({ mode: 'all', defaultValues });
    const { reset, setError, setValue } = methods;
    const formSaveService = useFormSaveService();
    const dataService = useDataService();
    const { selection } = tableSelection || null;
    const navigate = useNavigate();
    const [assignTest, setAssignTest] = useState({});

    const goToUrl = '/quickTests/voip';
    const functionBreadCrumbs = [{ link: goToUrl, text: t`VoIP Test Now` }];
    const reportingLinkConfig = {
        target: '/quickTests/voip',
    };
    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, null, {}, 'name', t`Test Details`, {
        reportingLinkConfig,
    });

    const getQuickTestById = useCallback(async () => {
        const quickTestUrl = `/ipm/v1/admin/quicktests/:_id`;
        const url = buildLinkUrl({ target: quickTestUrl }, { _id: selection[0] }, null);
        const response = await dataService.get(url, {
            query: JSON.stringify({
                status: _.values(QUICK_TEST_STATUS),
            }),
        });
        return response;
    }, [dataService, selection]);

    useEffect(() => {
        (async () => {
            let test = {};
            if (reRun && selection) {
                // set form fields and required state variables for when a test is chosen to re-run.
                test = await getQuickTestById();
            } else {
                test = { testConfig: {} };
            }

            assignFields(test);
            setBreadcrumbs(breadcrumbs);

            setLoading(false);
            setAssignTest(test);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataService, reRun, tableSelection, selection, setValue]);

    const dirtyCheck = (isDirty) => isDirty;

    const assignFields = (test) => {
        test.status = INITIAL_TEST_STATUS;
        test.createdById = user._id;
        test.createdBy = user.name;
        test.name = 'VoIP Test Now';
        test.agents = test.agents || { ids: [] };

        handleAssigningEmails(test);
    };

    const { initialized, finding } = useFindById({
        url: nPointsListApiUrl,
        resetFunc: reset,
        defaultValues,
    });

    const handleAssigningEmails = (testInfo) => {
        if (testInfo.emailAddresses) {
            if (_.isArray(testInfo.emailAddresses)) {
                testInfo.emailAddresses = testInfo.emailAddresses.join(EMAIL_SEPARATOR);
            }
        } else {
            testInfo.emailAddresses = user.email;
        }
    };

    function validateEmails(emailString) {
        const invalidEmails = [];

        // its possible for this guy to already be an array so put it back
        if (_.isArray(emailString)) {
            emailString = emailString.join(EMAIL_SEPARATOR);
        }

        const emails = _.map(splitEmailString(emailString), (email) => {
            email = _.trim(email);

            if (!isEmailValid(email)) {
                invalidEmails.push(email);
            }

            return email;
        });

        if (invalidEmails.length > 0) {
            alertsActions.createErrorAlert(i18n._(t`Invalid Emails: ${invalidEmails.join(' ')}`));
            return null;
        }

        return emails;
    }

    async function normalizeTest() {
        const detailsTest = { ...assignTest };
        // Build the caller agentResult object
        const callerAgent = _.first(detailsTest.testConfig.caller.ids);

        detailsTest.agentResults = [];
        detailsTest.summary = [];

        if (callerAgent) {
            detailsTest.agentResults = [
                _.extend({}, EMPTY_RESULTS_OBJECT, {
                    agent: callerAgent.id,
                    agentGuid: callerAgent.guid,
                }),
            ];
            detailsTest.summary = [
                _.extend({}, EMPTY_RESULTS_OBJECT, {
                    agent: callerAgent.id,
                    agentGuid: callerAgent.guid,
                    results: [],
                    resultCount: 0,
                }),
            ];
        }

        // If PulsetoPulse find the callee and build the agentResults
        if (detailsTest.quickTestType === VOIP_PULSE_TEST_TYPE) {
            const calleeAgent = _.first(detailsTest.testConfig.callee.ids);

            if (calleeAgent) {
                detailsTest.agentResults.push(
                    _.extend({}, EMPTY_RESULTS_OBJECT, {
                        agent: calleeAgent.id,
                        agentGuid: calleeAgent.guid,
                    })
                );
                detailsTest.summary.push(
                    _.extend({}, EMPTY_RESULTS_OBJECT, {
                        agent: calleeAgent.id,
                        agentGuid: calleeAgent.guid,
                    })
                );
            }
        } else {
            // If phone it's just an empty result object with phone set to true
            detailsTest.agentResults.push(
                _.extend({}, EMPTY_RESULTS_OBJECT, {
                    agentGuid: 'phone',
                    status: INITIAL_PHONE_STATUS,
                })
            );
            detailsTest.summary.push(
                _.extend({}, EMPTY_RESULTS_OBJECT, {
                    agentGuid: 'phone',
                    status: INITIAL_PHONE_STATUS,
                })
            );
        }

        if (_.isUndefined(detailsTest.emailAddresses)) detailsTest.emailAddresses = '';

        // Get the corresponding ID for the quickTestType
        const validEmails = validateEmails(detailsTest.emailAddresses);
        if (!_.isNil(validEmails)) {
            detailsTest.emailAddresses = validEmails;
            const testTypeId = await getVoipTestTypeId(detailsTest.quickTestType);
            detailsTest.type = testTypeId;
        }

        return detailsTest;
    }

    const getVoipTestTypeId = useCallback(
        async (voipType) => {
            const params = {
                query: JSON.stringify({ name: voipType }),
            };
            const response = await dataService.get(testTypeApiUrl, params);
            return response[0];
        },
        [dataService]
    );

    const handleFormSubmit = async (submittedFormData) => {
        const test = await normalizeTest();
        const tformData = {
            ...test,
            type: test?.type?._id,
            verify: false,
            createdBy: userName,
        };

        _.merge(tformData, submittedFormData);

        return formSaveService
            .save({
                url: quickTestsApiUrl,
                dataToSave: tformData,
                setError,
            })
            .then((response) => navigate(`${response._id}`));
    };

    const sipUrlChange = useCallback(() => {
        if (assignTest.quickTestType === VOIP_PULSE_TEST_TYPE) {
            setAssignTest((current) => ({
                ...current,
                testConfig: {
                    ...current.testConfig,
                    sipUrl: current.testConfig.callVia.head + current.testConfig.callVia.body,
                },
            }));
        }
    }, [assignTest.quickTestType]);

    useEffect(() => {
        if (assignTest?.testConfig?.callVia?.head || assignTest?.testConfig?.callVia?.body) {
            sipUrlChange();
        }
    }, [
        assignTest?.testConfig?.callVia?.head,
        assignTest?.testConfig?.callVia?.body,
        assignTest.quickTestType,
        sipUrlChange,
    ]);

    return (
        <FormProvider {...methods} flexHeight>
            <NgpForm
                formId="quickTests/voip-form"
                initialized={initialized}
                loading={finding || loading}
                showSave={false}
                formPadding={false}
                submitFunction={handleFormSubmit}
                isDirtyFunc={dirtyCheck}
            >
                <FormSection title={<Trans>Call Participants</Trans>}>
                    <VoipPulseTestConfig
                        assignTest={assignTest}
                        setAssignTest={setAssignTest}
                        testNow
                        reRun={reRun}
                        classes={classes}
                    />
                </FormSection>
                <Button
                    ref={submitRef}
                    color="primary"
                    variant="contained"
                    size="large"
                    onClick={methods.handleSubmit(handleFormSubmit)}
                >
                    <Trans>Run Test</Trans>
                </Button>
            </NgpForm>
        </FormProvider>
    );
}

export default Panel({
    typeAlias: 'VoipTestNowDetailPanel',
    filters: { required: [], optional: [] },
})(VoipTestNowDetailPanel);
